<script lang="ts">
import { defineComponent } from 'vue'

/**
 * A range slider.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/range
 * @see https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/slider_role
 */
export default defineComponent({
  props: {
    /**
     * The name of the input range selector.
     */
    name: {
      type: String,
      required: true,
    },
    /**
     * @model
     */
    modelValue: {
      type: Number,
      required: true,
    },
    min: {
      type: Number,
      default: 1,
    },
    max: {
      type: Number,
      required: true,
    },
    styleType: {
      type: String,
      default: 'default',
      validator(val: string): boolean {
        return ['default', 'mysterybox'].includes(val)
      },
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue'],
  computed: {
    value: {
      get() {
        return this.modelValue
      },
      set(value: string) {
        this.$emit(
          'update:modelValue',
          Math.min(Math.max(this.min, Number.parseInt(value)), this.max)
        )
      },
    },
  },
  mounted() {
    // Forces the range selector to set initial position
    this.$forceUpdate()
  },
})
</script>

<template>
  <div
    :class="[
      'flex w-full touch-manipulation items-center justify-center',
      { 'gap-3': styleType === 'default' },
    ]"
  >
    <button
      type="button"
      :disabled="disabled"
      :class="[
        'touch-manipulation items-center justify-center rounded',
        {
          'size-10 shrink-0 bg-slate-800 text-2xl text-white':
            styleType === 'mysterybox' && !disabled,
        },
        {
          'size-10 shrink-0 cursor-not-allowed bg-slate-400 text-2xl text-gray-600':
            styleType === 'mysterybox' && disabled,
        },
        {
          'flex h-[32px] w-[32px] bg-slate-600/50 p-3 text-2xl':
            styleType === 'default' && !disabled,
        },
        {
          'flex h-[32px] w-[32px] cursor-not-allowed bg-slate-400 p-3 text-2xl text-gray-600':
            styleType === 'default' && disabled,
        },
      ]"
      @click="value--"
    >
      -
    </button>

    <label
      :class="[
        'w-full touch-manipulation justify-center rounded',
        { 'mx-2 flex': styleType === 'mysterybox' },
        { 'flex h-[32px] bg-slate-600/50 p-3': styleType === 'default' },
      ]"
    >
      <span class="sr-only">A slider element.</span>
      <input
        :id="name"
        v-model="value"
        type="range"
        :name
        :min
        :max
        :disabled="disabled"
        class="w-full touch-manipulation"
        :class="[
          { 'mysterybox-style': styleType === 'mysterybox' },
          { 'default-style': styleType === 'default' },
          { 'cursor-not-allowed opacity-50': disabled },
        ]"
      />
    </label>

    <button
      type="button"
      :disabled="disabled"
      :class="[
        'touch-manipulation items-center justify-center rounded',
        {
          'size-10 shrink-0 bg-slate-800 text-2xl text-white':
            styleType === 'mysterybox' && !disabled,
        },
        {
          'size-10 shrink-0 cursor-not-allowed bg-slate-400 text-2xl text-gray-600':
            styleType === 'mysterybox' && disabled,
        },
        {
          'flex h-[32px] w-[32px] bg-slate-600/50 p-3 text-2xl':
            styleType === 'default' && !disabled,
        },
        {
          'flex h-[32px] w-[32px] cursor-not-allowed bg-slate-400 p-3 text-2xl text-gray-600':
            styleType === 'default' && disabled,
        },
      ]"
      @click="value++"
    >
      +
    </button>
  </div>
</template>

<style lang="scss" scoped>
// https://css-tricks.com/styling-cross-browser-compatible-range-inputs-css/
input[type='range'] {
  @apply bg-slate-300 rounded-100;

  appearance: none; /* Standard property for compatibility */
  -webkit-appearance: none; /* Hides the slider so that custom slider can be made */
  width: 100%; /* Specific width is required for Firefox. */
  outline: none;
  height: 6px;
  cursor: pointer;
}

input[type='range']::-webkit-slider-thumb {
  -webkit-appearance: none;
  @apply bg-tertiary rounded-full;
}

input[type='range']:focus {
  outline: none; /* Removes the blue border. You should probably do some kind of focus styling for accessibility reasons though. */
}

/* Special styling for WebKit/Blink */
input[type='range'].mysterybox-style::-webkit-slider-thumb {
  -webkit-appearance: none;
  height: 16px;
  width: 16px;
  border: 0;
  box-shadow: 0 0 0 4px rgba(255, 255, 255, 0.4);

  @apply cursor-pointer rounded-full bg-tertiary p-2;
}
input[type='range'].default-style::-webkit-slider-thumb {
  -webkit-appearance: none;
  height: 24px;
  width: 24px;
  margin-top: -10px;
  border: 4px solid;

  @apply cursor-pointer rounded-full border-black bg-tertiary p-2;
}

/* All the same stuff for Firefox */
input[type='range'].mysterybox-style::-moz-range-thumb {
  appearance: none;
  height: 4px;
  width: 4px;
  border: 0;
  box-shadow: 0 0 0 4px rgba(255, 255, 255, 0.4);

  @apply cursor-pointer rounded-full bg-tertiary p-2;
}
input[type='range'].default-style::-moz-range-thumb {
  height: 24px;
  width: 24px;
  margin-top: -10px;
  border: 4px solid;

  @apply cursor-pointer rounded-full border-black bg-tertiary p-2;
}

/* All the same stuff for IE */
input[type='range'].mysterybox-style::-ms-thumb {
  height: 16px;
  width: 16px;
  margin-top: -6px;
  border: 0;
  box-shadow: 0 0 0 4px rgba(255, 255, 255, 0.4);

  @apply cursor-pointer rounded-full bg-tertiary p-2;
}
input[type='range'].default-style::-ms-thumb {
  height: 24px;
  width: 24px;
  margin-top: -10px;
  border: 4px solid;

  @apply cursor-pointer rounded-full border-black bg-tertiary p-2;
}
</style>
